import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { BookingSessionStore } from '../../portal-appointments/appointment-booking/stores/booking-session.store';
import { AvailableService } from '../../portal-appointments/services/available-services.service';
import { LayoutService } from '../../shared/services/layout.service';

export interface OrbiFindLabHandoff {
  /** Required: the results page sends a session with no service back to Appointments. */
  serviceId: number;
  serviceName?: string;
  showUser?: boolean;
  /** Empty opens the results page on its address/ZIP prompt. */
  address: string;
  latitude?: number;
  longitude?: number;
  /** Defaults to the last 5-digit run in `address`. */
  zipCode?: string;
  /** `YYYY-MM-DD`; today when absent. */
  date?: string;
}

/** The agent may send a catalog id or a service name; names match loosely. */
export function matchOrbiInitialService(
  initialService: string | undefined,
  services: readonly AvailableService[]
): AvailableService | null {
  const raw = initialService?.trim();
  if (!raw) {
    return null;
  }
  const asId = Number(raw);
  if (Number.isInteger(asId)) {
    const byId = services.find((s) => s.serviceId === asId);
    if (byId) {
      return byId;
    }
  }
  const wanted = normalizeServiceName(raw);
  if (!wanted) {
    return null;
  }
  const named = services.map((s) => ({
    service: s,
    name: normalizeServiceName(s.serviceName),
  }));
  // Whole words only: "Pediatric" finds "Pediatric Labwork", "lab" finds nothing.
  return (
    named.find((n) => n.name === wanted)?.service ??
    named.find(
      (n) =>
        !!n.name &&
        (n.name.startsWith(`${wanted} `) || wanted.startsWith(`${n.name} `))
    )?.service ??
    null
  );
}

function normalizeServiceName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/**
 * Mirrors the Find a Lab modal's `continueSubmit` hand-off so chat lands the
 * user in the identical booking flow.
 */
@Injectable({ providedIn: 'root' })
export class OrbiFindLabHandoffService {
  /** The portal modal's default. */
  static readonly RADIUS_MILES = 25;

  constructor(
    private readonly router: Router,
    private readonly layoutService: LayoutService,
    private readonly bookingStore: BookingSessionStore
  ) {}

  openResults(search: OrbiFindLabHandoff): void {
    const zipCode = search.zipCode ?? extractFiveDigitZip(search.address);
    this.seedService(search);
    this.seedPlaceholderPatient(zipCode);
    this.layoutService.setOrbiPanelOpen(false);

    const queryParams: Record<string, string | number> = {
      address: search.address,
      date: search.date ?? OrbiFindLabHandoffService.todayLocal(),
      radius: OrbiFindLabHandoffService.RADIUS_MILES,
      serviceId: search.serviceId,
    };
    if (search.latitude != null && search.longitude != null) {
      queryParams['lat'] = search.latitude;
      queryParams['lon'] = search.longitude;
    }
    if (zipCode) {
      queryParams['zip'] = zipCode;
    }

    this.router.navigate(['/portal/appointments/book/find-a-lab'], {
      queryParams,
    });
  }

  /** Local, not UTC: a UTC date reads as tomorrow every US evening. */
  static todayLocal(now = new Date()): string {
    return [
      now.getFullYear(),
      String(now.getMonth() + 1).padStart(2, '0'),
      String(now.getDate()).padStart(2, '0'),
    ].join('-');
  }

  private seedService(search: OrbiFindLabHandoff): void {
    this.bookingStore.setSelectedService({
      id: search.serviceId,
      name: search.serviceName ?? '',
      shortName: search.serviceName ?? '',
      showUser: search.showUser ?? true,
      displayOrder: 0,
      appointmentOnlyService: false,
    });
  }

  // The flow's validation needs a patient; never clobber a real selection.
  private seedPlaceholderPatient(zipCode: string | undefined): void {
    const session = this.bookingStore.getCurrentState();
    if (session.selectedPatient && session.selectedPatient.id !== 0) {
      return;
    }
    this.bookingStore.setSelectedPatient({
      id: 0,
      firstName: 'Patient',
      lastName: 'User',
      dateOfBirth: '1990-01-01',
      sex: 'MALE',
      isPrimaryAccountHolder: true,
      address: {
        street: '',
        city: '',
        state: '',
        zipCode: zipCode ?? '',
      },
    });
  }
}

/** So a full address still gets its Getlabs check. */
export function extractFiveDigitZip(addressOrZip: string): string | undefined {
  // Last, not first: in "12345 MAIN ST, RALEIGH, NC 27601" the ZIP comes last.
  return addressOrZip.match(/\b\d{5}\b/g)?.pop();
}
